import React, { useState, useEffect, useCallback } from 'react';
import { http } from '../../lib/http';

const MEAL_OPTIONS = ['Breakfast', 'Lunch', 'Dinner'];
const DAY_OPTIONS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

const PLAN_LABELS = {
  '5_days_veg': '5 Days Veg',
  '5_days_nonveg': '5 Days Non-Veg',
  '2NV_3V': 'Mixed (2 Non-Veg + 3 Veg Days)',
};

const MEAL_COUNT_LABELS = {
  '2_meals': '2 Meals a Day',
  '3_meals': '3 Meals a Day',
};

const DEFAULT_FORM = {
  selectedMeals: [],     // lowercase, e.g. ['lunch','dinner']
  nvDays: [],            // lowercase weekdays
  nvMealCountChoice: '', // '1' or '2' (UI helper for 2_meals nonveg/mixed)
  nvMealCountChoice3: '',// '1' or '2' (UI helper for 3_meals nonveg/mixed: 1NV+2V or 2NV+1V)
  vegMealChoice: '',     // which single meal stays veg (lowercase) — for 3_meals with 2NV
  nvMealChoice3: '',     // which single meal is NV (lowercase) — for 3_meals with 1NV
  nvMealSelection: [],   // which meals are NV (lowercase)
  allergies: '',         // comma-separated string (converted to array on save)
  dietaryNotes: '',
};

function toDisplay(str) {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
}

function buildFormFromPrefs(prefs) {
  if (!prefs || !prefs.mealCount) return DEFAULT_FORM;

  const nvMealCount = prefs.nvMealCount || 0;
  const nvMealSel = prefs.nvMealSelection || [];
  const selectedMeals = prefs.selectedMeals || [];

  // Determine vegMealChoice: for 3_meals with 2 NV meals, it's the one meal NOT in nvMealSelection
  let vegMealChoice = '';
  if (prefs.mealCount === '3_meals' && prefs.vegType !== '5_days_veg' && nvMealSel.length === 2) {
    const all3 = ['breakfast', 'lunch', 'dinner'];
    vegMealChoice = all3.find(m => !nvMealSel.includes(m)) || '';
  }

  // Determine nvMealChoice3: for 3_meals with 1 NV meal, it's the one meal that IS NV
  let nvMealChoice3 = '';
  if (prefs.mealCount === '3_meals' && prefs.vegType !== '5_days_veg' && nvMealSel.length === 1) {
    nvMealChoice3 = nvMealSel[0] || '';
  }

  // Determine nvMealCountChoice3: for 3_meals nonveg/mixed (1NV+2V or 2NV+1V)
  let nvMealCountChoice3 = '';
  if (prefs.mealCount === '3_meals' && prefs.vegType !== '5_days_veg') {
    nvMealCountChoice3 = nvMealCount === 2 ? '2' : nvMealCount === 1 ? '1' : '';
  }

  // Determine nvMealCountChoice: for 2_meals nonveg/mixed
  let nvMealCountChoice = '';
  if (prefs.mealCount === '2_meals' && prefs.vegType !== '5_days_veg') {
    nvMealCountChoice = nvMealCount === 2 ? '2' : nvMealCount === 1 ? '1' : '';
  }

  return {
    selectedMeals: selectedMeals,
    nvDays: prefs.nvDays || [],
    nvMealCountChoice,
    nvMealCountChoice3,
    vegMealChoice,
    nvMealChoice3,
    nvMealSelection: nvMealSel,
    allergies: (prefs.allergies || []).join(', '),
    dietaryNotes: prefs.dietaryNotes || '',
  };
}

function buildPayload(formData, mealCount, vegType) {
  const selectedMeals =
    mealCount === '3_meals'
      ? ['breakfast', 'lunch', 'dinner']
      : formData.selectedMeals;

  let nvMealCount = 0;
  let nvMealSelection = [];
  const nvDays = vegType === '2NV_3V' ? formData.nvDays : [];

  if (vegType === '5_days_veg') {
    nvMealCount = 0;
    nvMealSelection = [];
  } else if (mealCount === '3_meals') {
    // Customer chose 1NV+2V or 2NV+1V
    nvMealCount = parseInt(formData.nvMealCountChoice3) || 0;
    if (nvMealCount === 2) {
      // 2 NV meals: the one NOT chosen as veg stays NV
      nvMealSelection = ['breakfast', 'lunch', 'dinner'].filter(
        m => m !== formData.vegMealChoice
      );
    } else if (nvMealCount === 1) {
      // 1 NV meal: customer picked which one is NV
      nvMealSelection = formData.nvMealChoice3 ? [formData.nvMealChoice3] : [];
    }
  } else {
    // 2_meals nonveg or mixed
    nvMealCount = parseInt(formData.nvMealCountChoice) || 0;
    if (nvMealCount === 2) {
      nvMealSelection = [...selectedMeals];
    } else if (nvMealCount === 1) {
      nvMealSelection = formData.nvMealSelection.slice(0, 1);
    } else {
      nvMealSelection = [];
    }
  }

  return {
    mealCount,
    vegType,
    selectedMeals,
    nvDays,
    nvMealCount,
    nvMealSelection,
    allergies: formData.allergies
      ? formData.allergies.split(',').map(s => s.trim()).filter(Boolean)
      : [],
    dietaryNotes: formData.dietaryNotes || '',
  };
}

function validate(formData, mealCount, vegType, setError) {
  if (mealCount === '2_meals' && formData.selectedMeals.length !== 2) {
    setError('Please select exactly 2 meals.');
    return false;
  }
  if (vegType === '2NV_3V' && formData.nvDays.length !== 2) {
    setError('Please select exactly 2 Non-Veg days.');
    return false;
  }
  if (mealCount === '3_meals' && vegType !== '5_days_veg' && !formData.nvMealCountChoice3) {
    setError('Please select how many Non-Veg meals per day.');
    return false;
  }
  if (mealCount === '3_meals' && vegType !== '5_days_veg' && formData.nvMealCountChoice3 === '2' && !formData.vegMealChoice) {
    setError('Please select which meal stays Vegetarian.');
    return false;
  }
  if (mealCount === '3_meals' && vegType !== '5_days_veg' && formData.nvMealCountChoice3 === '1' && !formData.nvMealChoice3) {
    setError('Please select which meal is Non-Veg.');
    return false;
  }
  if (mealCount === '2_meals' && vegType !== '5_days_veg' && !formData.nvMealCountChoice) {
    setError('Please select how many Non-Veg meals per day.');
    return false;
  }
  if (
    mealCount === '2_meals' &&
    vegType !== '5_days_veg' &&
    formData.nvMealCountChoice === '1' &&
    formData.nvMealSelection.length !== 1
  ) {
    setError('Please select which meal is Non-Veg.');
    return false;
  }
  return true;
}

// ─── ViewMode ────────────────────────────────────────────────────────────────

function ViewMode({ savedPrefs, mealCount, vegType, onEdit }) {
  const hasMealCount = mealCount === '2_meals';
  const isNonVeg = vegType !== '5_days_veg';
  const isMixed = vegType === '2NV_3V';

  const noPrefs = !savedPrefs || !savedPrefs.mealCount;

  return (
    <div className="space-y-4">
      {noPrefs ? (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-sm text-gray-500 italic">
          No preferences saved yet.
        </div>
      ) : (
        <div className="bg-orange-50 rounded-lg p-4 border border-orange-200 space-y-2 text-sm">
          {/* Meals */}
          {hasMealCount && savedPrefs.selectedMeals?.length > 0 && (
            <div>
              <span className="font-semibold text-gray-800">Meals: </span>
              <span className="text-gray-700">
                {savedPrefs.selectedMeals.map(toDisplay).join(', ')}
              </span>
            </div>
          )}
          {!hasMealCount && (
            <div>
              <span className="font-semibold text-gray-800">Meals: </span>
              <span className="text-gray-700">Breakfast, Lunch, Dinner (Fixed)</span>
            </div>
          )}

          {/* NV Days (2NV_3V only) */}
          {isMixed && savedPrefs.nvDays?.length > 0 && (
            <div>
              <span className="font-semibold text-gray-800">Non-Veg Days: </span>
              <span className="text-gray-700">
                {savedPrefs.nvDays.map(toDisplay).join(', ')}
              </span>
            </div>
          )}

          {/* NV Meal info */}
          {isNonVeg && savedPrefs.nvMealCount > 0 && (
            <div>
              <span className="font-semibold text-gray-800">
                {isMixed ? 'NV Meals on Non-Veg Days: ' : 'Non-Veg Meals/Day: '}
              </span>
              <span className="text-gray-700">{savedPrefs.nvMealCount}</span>
            </div>
          )}
          {isNonVeg && savedPrefs.nvMealSelection?.length > 0 && (
            <div>
              <span className="font-semibold text-gray-800">Non-Veg Meal Slots: </span>
              <span className="text-gray-700">
                {savedPrefs.nvMealSelection.map(toDisplay).join(', ')}
              </span>
            </div>
          )}

          {/* Veg Plan info */}
          {!isNonVeg && mealCount === '3_meals' && (
            <div className="text-gray-700">
              All 3 meals daily — fully vegetarian.
            </div>
          )}

          {/* Allergies */}
          {savedPrefs.allergies?.length > 0 && (
            <div>
              <span className="font-semibold text-red-600">Allergies: </span>
              <span className="text-gray-700">{savedPrefs.allergies.join(', ')}</span>
            </div>
          )}

          {/* Dietary Notes */}
          {savedPrefs.dietaryNotes && (
            <div>
              <span className="font-semibold text-gray-800">Notes: </span>
              <span className="text-gray-700">{savedPrefs.dietaryNotes}</span>
            </div>
          )}
        </div>
      )}

      <button
        onClick={onEdit}
        className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2.5 rounded-lg transition-colors"
      >
        {noPrefs ? 'Set Preferences' : 'Edit Preferences'}
      </button>
    </div>
  );
}

// ─── EditMode ────────────────────────────────────────────────────────────────

function CheckboxRow({ label, checked, disabled, onChange }) {
  return (
    <label
      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer select-none transition-colors ${
        disabled
          ? 'opacity-40 cursor-not-allowed border-gray-200 bg-gray-50'
          : checked
          ? 'border-orange-500 bg-orange-50'
          : 'border-gray-300 hover:bg-gray-50'
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="w-5 h-5 accent-orange-500"
      />
      <span className="font-medium text-gray-900">{label}</span>
    </label>
  );
}

function RadioRow({ name, label, checked, onChange }) {
  return (
    <label
      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer select-none transition-colors ${
        checked ? 'border-orange-500 bg-orange-50' : 'border-gray-300 hover:bg-gray-50'
      }`}
    >
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onChange}
        className="w-5 h-5 accent-orange-500"
      />
      <span className="font-medium text-gray-900">{label}</span>
    </label>
  );
}

function Section({ title, description, children }) {
  return (
    <div className="space-y-2">
      <p className="text-base font-semibold text-gray-900">{title}</p>
      {description && <p className="text-sm text-gray-500">{description}</p>}
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function EditMode({ formData, setFormData, mealCount, vegType, restrictVegNvEditing = false }) {
  const isVeg = vegType === '5_days_veg';
  const isNonVeg = vegType === '5_days_nonveg';
  const isMixed = vegType === '2NV_3V';
  const is2Meals = mealCount === '2_meals';
  const is3Meals = mealCount === '3_meals';

  const toggleMeal = (meal) => {
    const lower = meal.toLowerCase();
    const curr = formData.selectedMeals;
    let next;
    if (curr.includes(lower)) {
      next = curr.filter(m => m !== lower);
    } else {
      next = [...curr, lower];
    }
    // If deselecting a meal that was the NV meal selection, clear it
    let newNvMealSel;
    if (formData.nvMealCountChoice === '2') {
      newNvMealSel = [...next];
    } else {
      newNvMealSel = formData.nvMealSelection.filter(m => next.includes(m));
    }
    setFormData(f => ({ ...f, selectedMeals: next, nvMealSelection: newNvMealSel }));
  };

  const toggleNvDay = (day) => {
    const lower = day.toLowerCase();
    const curr = formData.nvDays;
    const next = curr.includes(lower)
      ? curr.filter(d => d !== lower)
      : [...curr, lower];
    setFormData(f => ({ ...f, nvDays: next }));
  };

  const setVegMealChoice = (meal) => {
    setFormData(f => ({ ...f, vegMealChoice: meal.toLowerCase() }));
  };

  const setNvMealCountChoice = (val) => {
    setFormData(f => ({
      ...f,
      nvMealCountChoice: val,
      nvMealSelection: val === '2' ? [...formData.selectedMeals] : [],
    }));
  };

  const setNvMealCountChoice3 = (val) => {
    setFormData(f => ({
      ...f,
      nvMealCountChoice3: val,
      vegMealChoice: '',
      nvMealChoice3: '',
    }));
  };

  const setNvMealChoice3 = (meal) => {
    setFormData(f => ({ ...f, nvMealChoice3: meal.toLowerCase() }));
  };

  const toggleNvMealSel = (meal) => {
    const lower = meal.toLowerCase();
    const curr = formData.nvMealSelection;
    const next = curr.includes(lower)
      ? curr.filter(m => m !== lower)
      : [...curr, lower];
    setFormData(f => ({ ...f, nvMealSelection: next }));
  };

  const mealSelectorAtMax = formData.selectedMeals.length >= 2;

  return (
    <div className="space-y-6">
      {/* ── Restriction notice for customers (non-admin, non-renewal) ── */}
      {restrictVegNvEditing && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
          Veg/Non-Veg preferences can only be changed during plan renewal.
          {is2Meals
            ? ' You can update which 2 meals you receive, allergies, and dietary notes below.'
            : ' You can update your allergies and dietary notes below.'}
        </div>
      )}

      {/* ── 2 Meals: Which meals ── */}
      {is2Meals && (
        <Section
          title="Which 2 meals would you like? *"
          description="Select exactly 2 meals for your daily plan."
        >
          {MEAL_OPTIONS.map(meal => {
            const lower = meal.toLowerCase();
            const checked = formData.selectedMeals.includes(lower);
            return (
              <CheckboxRow
                key={meal}
                label={meal}
                checked={checked}
                disabled={!checked && mealSelectorAtMax}
                onChange={() => toggleMeal(meal)}
              />
            );
          })}
          {formData.selectedMeals.length > 0 && formData.selectedMeals.length !== 2 && (
            <p className="text-sm text-orange-600">Select exactly 2 meals ({formData.selectedMeals.length}/2 selected)</p>
          )}
        </Section>
      )}

      {/* ── 3 Meals: info ── */}
      {is3Meals && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
          Your plan includes all 3 meals: <strong>Breakfast, Lunch & Dinner</strong> every day.
        </div>
      )}

      {/* ── Veg plan + 3 meals: no questions needed ── */}
      {isVeg && is3Meals && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
          All meals are fully vegetarian — no further preferences needed.
        </div>
      )}

      {/* ── NV Days (2NV_3V only — locked for customers, editable by admin/renewal) ── */}
      {isMixed && (
        <Section
          title={restrictVegNvEditing ? "Non-Veg Days (locked)" : "Which 2 days are Non-Veg? *"}
          description={restrictVegNvEditing ? "Non-Veg days can only be changed during plan renewal or by admin." : "Select exactly 2 weekdays for non-veg meals."}
        >
          {DAY_OPTIONS.map(day => {
            const lower = day.toLowerCase();
            const checked = formData.nvDays.includes(lower);
            const atMax = formData.nvDays.length >= 2;
            return (
              <CheckboxRow
                key={day}
                label={day}
                checked={checked}
                disabled={restrictVegNvEditing || (!checked && atMax)}
                onChange={() => !restrictVegNvEditing && toggleNvDay(day)}
              />
            );
          })}
          {!restrictVegNvEditing && formData.nvDays.length > 0 && formData.nvDays.length !== 2 && (
            <p className="text-sm text-orange-600">Select exactly 2 days ({formData.nvDays.length}/2 selected)</p>
          )}
        </Section>
      )}

      {/* ── 3 Meals + Non-Veg/Mixed: how many NV meals ── */}
      {!restrictVegNvEditing && is3Meals && !isVeg && (
        <Section
          title="How many Non-Veg meals per day? *"
          description={isMixed ? 'On your Non-Veg days, how many meals are Non-Veg?' : 'Choose how many of your 3 meals are Non-Veg.'}
        >
          <RadioRow
            name="nvMealCountChoice3"
            label="1 Non-Veg + 2 Veg"
            checked={formData.nvMealCountChoice3 === '1'}
            onChange={() => setNvMealCountChoice3('1')}
          />
          <RadioRow
            name="nvMealCountChoice3"
            label="2 Non-Veg + 1 Veg"
            checked={formData.nvMealCountChoice3 === '2'}
            onChange={() => setNvMealCountChoice3('2')}
          />
        </Section>
      )}

      {/* ── 3 Meals + 2NV: which meal stays Veg ── */}
      {!restrictVegNvEditing && is3Meals && !isVeg && formData.nvMealCountChoice3 === '2' && (
        <Section
          title="Which meal stays Vegetarian? *"
          description="2 meals will be Non-Veg. Select the 1 meal that remains Veg."
        >
          {MEAL_OPTIONS.map(meal => (
            <RadioRow
              key={meal}
              name="vegMealChoice"
              label={meal}
              checked={formData.vegMealChoice === meal.toLowerCase()}
              onChange={() => setVegMealChoice(meal)}
            />
          ))}
        </Section>
      )}

      {/* ── 3 Meals + 1NV: which meal is Non-Veg ── */}
      {!restrictVegNvEditing && is3Meals && !isVeg && formData.nvMealCountChoice3 === '1' && (
        <Section
          title="Which meal is Non-Veg? *"
          description="1 meal will be Non-Veg. Select which one."
        >
          {MEAL_OPTIONS.map(meal => (
            <RadioRow
              key={meal}
              name="nvMealChoice3"
              label={meal}
              checked={formData.nvMealChoice3 === meal.toLowerCase()}
              onChange={() => setNvMealChoice3(meal)}
            />
          ))}
        </Section>
      )}

      {/* ── 2 Meals + Non-Veg/Mixed: how many NV meals ── */}
      {!restrictVegNvEditing && is2Meals && !isVeg && (
        <Section
          title="How many Non-Veg meals per day? *"
          description={isMixed ? 'On your Non-Veg days, how many meals are Non-Veg?' : 'Choose how many of your 2 meals are Non-Veg.'}
        >
          <RadioRow
            name="nvMealCountChoice"
            label="1 Non-Veg + 1 Veg"
            checked={formData.nvMealCountChoice === '1'}
            onChange={() => setNvMealCountChoice('1')}
          />
          <RadioRow
            name="nvMealCountChoice"
            label="2 Non-Veg (Both meals Non-Veg)"
            checked={formData.nvMealCountChoice === '2'}
            onChange={() => setNvMealCountChoice('2')}
          />
        </Section>
      )}

      {/* ── 2 Meals + Non-Veg/Mixed + 1 NV: which meal is NV ── */}
      {is2Meals && !isVeg && formData.nvMealCountChoice === '1' && formData.selectedMeals.length === 2 && (
        <Section
          title="Which meal is Non-Veg? *"
          description="Select which of your 2 meals will be Non-Veg."
        >
          {formData.selectedMeals.map(m => (
            <RadioRow
              key={m}
              name="nvMealSel"
              label={toDisplay(m)}
              checked={formData.nvMealSelection.includes(m)}
              onChange={() => toggleNvMealSel(m)}
            />
          ))}
        </Section>
      )}

      {/* ── Allergies ── */}
      <div className="space-y-1 pt-2 border-t border-gray-200">
        <label className="block text-base font-semibold text-gray-900">Allergies</label>
        <input
          type="text"
          placeholder="e.g. peanuts, dairy (comma-separated)"
          value={formData.allergies}
          onChange={e => setFormData(f => ({ ...f, allergies: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
        />
      </div>

      {/* ── Dietary Notes ── */}
      <div className="space-y-1">
        <label className="block text-base font-semibold text-gray-900">Dietary Notes</label>
        <textarea
          placeholder="Any special dietary requirements or notes..."
          value={formData.dietaryNotes}
          onChange={e => setFormData(f => ({ ...f, dietaryNotes: e.target.value }))}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
        />
      </div>
    </div>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────

export default function MealPreferencesModal({
  isOpen,
  onClose,
  customerId,
  customerName,
  mealCount,
  vegType,
  isAdmin,
  setMessage,
  setError,
  restrictVegNvEditing = false,
}) {
  const [mode, setMode] = useState('view');
  const [savedPrefs, setSavedPrefs] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchPrefs = useCallback(async () => {
    setLoading(true);
    try {
      let res;
      if (isAdmin) {
        res = await http.get(`/admin/customer-preferences/${customerId}`);
        const prefs = res?.data?.data?.preferences || res?.data?.preferences || {};
        setSavedPrefs(Object.keys(prefs).length ? prefs : null);
        setFormData(buildFormFromPrefs(Object.keys(prefs).length ? prefs : null));
        setMode(Object.keys(prefs).length ? 'view' : 'edit');
      } else {
        res = await http.get('/customer/preferences');
        const prefs = res?.data?.preferences || {};
        const hasPrefs = Object.keys(prefs).length > 0 && prefs.mealCount;
        setSavedPrefs(hasPrefs ? prefs : null);
        setFormData(buildFormFromPrefs(hasPrefs ? prefs : null));
        setMode(hasPrefs ? 'view' : 'edit');
      }
    } catch (err) {
      console.error('Failed to load preferences:', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, customerId]);

  useEffect(() => {
    if (isOpen) {
      fetchPrefs();
    }
  }, [isOpen, fetchPrefs]);

  const handleSave = async () => {
    setError?.('');

    // Restricted mode: only save allowed fields (meal selection for 2-meal + dietary)
    if (restrictVegNvEditing) {
      if (mealCount === '2_meals' && formData.selectedMeals.length !== 2) {
        setError?.('Please select exactly 2 meals.');
        return;
      }
      // NV days validation skipped — customer cannot change NV days in restricted mode
      if (mealCount === '2_meals' && vegType !== '5_days_veg'
          && formData.nvMealCountChoice === '1'
          && formData.nvMealSelection.length !== 1) {
        setError?.('Please select which meal is Non-Veg.');
        return;
      }
      setSaving(true);
      try {
        const allergiesArr = formData.allergies
          ? formData.allergies.split(',').map(s => s.trim()).filter(Boolean)
          : [];

        // Always save dietary preferences
        await http.patch('/customer/preferences/dietary', {
          allergies: allergiesArr,
          dietaryNotes: formData.dietaryNotes || '',
        });

        // For 2-meal plans, also save selectedMeals
        if (mealCount === '2_meals' && formData.selectedMeals.length === 2) {
          await http.patch('/customer/preferences/meal-type', {
            selectedMeals: formData.selectedMeals,
          });

          // For non-veg 2-meal plans, also save nvMealSelection
          if (vegType !== '5_days_veg' && formData.nvMealSelection.length > 0) {
            await http.patch('/customer/preferences/nv-meals', {
              nvMealSelection: formData.nvMealSelection,
            });
          }
        }

        // NV days are NOT saved in restricted mode — only changeable during renewal or by admin

        setMessage?.('Preferences saved successfully!');
        await fetchPrefs();
        setMode('view');
      } catch (err) {
        setError?.(err?.response?.data?.message || 'Failed to save preferences.');
      } finally {
        setSaving(false);
      }
      return;
    }

    // Full save mode (admin or renewal)
    if (!validate(formData, mealCount, vegType, setError)) return;

    setSaving(true);
    try {
      const payload = buildPayload(formData, mealCount, vegType);
      if (isAdmin) {
        await http.post(`/admin/customer-preferences/${customerId}`, payload);
      } else {
        await http.post('/customer/preferences', payload);
      }
      setMessage?.('Preferences saved successfully!');
      await fetchPrefs();
      setMode('view');
    } catch (err) {
      setError?.(err?.response?.data?.message || 'Failed to save preferences.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const planLabel = PLAN_LABELS[vegType] || vegType || 'Unknown';
  const mealCountLabel = MEAL_COUNT_LABELS[mealCount] || mealCount || 'Unknown';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">

        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-black to-gray-900 text-white px-6 py-4 rounded-t-xl border-b-4 border-orange-500 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              🍽️ Meal Preferences
            </h2>
            {customerName && (
              <p className="text-sm text-gray-300 mt-0.5">{customerName}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl font-bold transition-colors"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Plan info */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-sm space-y-1">
            <p><span className="font-semibold text-gray-800">Plan Type: </span><span className="text-gray-700">{planLabel}</span></p>
            <p><span className="font-semibold text-gray-800">Meal Count: </span><span className="text-gray-700">{mealCountLabel}</span></p>
          </div>

          {/* View / Edit toggle */}
          <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-1">
            <button
              onClick={() => setMode('view')}
              className={`flex-1 rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
                mode === 'view' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-600 hover:text-black'
              }`}
            >
              View
            </button>
            <button
              onClick={() => setMode('edit')}
              className={`flex-1 rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
                mode === 'edit' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-600 hover:text-black'
              }`}
            >
              Edit
            </button>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-orange-500 border-t-transparent"></div>
            </div>
          ) : mode === 'view' ? (
            <ViewMode
              savedPrefs={savedPrefs}
              mealCount={mealCount}
              vegType={vegType}
              onEdit={() => setMode('edit')}
            />
          ) : (
            <>
              <EditMode
                formData={formData}
                setFormData={setFormData}
                mealCount={mealCount}
                vegType={vegType}
                restrictVegNvEditing={restrictVegNvEditing}
              />
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Preferences'}
                </button>
                <button
                  onClick={() => {
                    setFormData(buildFormFromPrefs(savedPrefs));
                    setMode('view');
                  }}
                  disabled={saving}
                  className="flex-1 border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold py-2.5 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
